(ns ui.gantt-window
  "Which runs and which schedule marks belong on the gantt for a given window.
   Kept out of the view so it can be exercised without one."
  (:require
   ["date-fns" :refer [differenceInSeconds isAfter isBefore isEqual
                       isWithinInterval startOfHour]]))


(defn window-hours
  "Fractional hours the window spans. Windows end on their last second
   (`xx:59:59`), hence the `inc`."
  [{:keys [started-at ended-at]}]
  (/ (inc (differenceInSeconds ended-at started-at)) 3600))


(def ^:private tick-steps
  "Label steps in seconds, finest first; each divides an hour."
  [10 15 30 60 120 300])

(defn tick-step
  "The finest step whose labels sit at least `min-gap` px apart, else 10 minutes."
  [px-per-second min-gap]
  (or (first (filter #(>= (* % px-per-second) min-gap) tick-steps))
      600))

(defn ticks
  "Clock marks every `step` seconds within the window (10:05, 10:10 — not 10:03,
   10:08). Aligned to the wall-clock hour, not the epoch, which is off the hour in
   a +05:30 zone."
  [{:keys [started-at ended-at]} step]
  (when (and started-at ended-at (pos? step))
    (let [step-ms (* step 1000)
          base    (.getTime (startOfHour started-at))
          start   (+ base (* step-ms (js/Math.ceil (/ (- (.getTime started-at) base) step-ms))))]
      (map #(js/Date. %) (range start (inc (.getTime ended-at)) step-ms)))))


(defn ran-in-window?
  "Whether the time a run *occupied* overlaps the window — the same question the
   log table asks of the API, which windows on started_at. Testing the fire-time
   instead drops a run that started before the window and is still going in it."
  [{:keys [started-at finished-at]} {win-start :started-at win-end :ended-at} now]
  (boolean
   (and started-at win-start win-end
        (not (isAfter started-at win-end))
        (not (isBefore (or finished-at now) win-start)))))


(def ^:private schedule-label-gap
  "Minimum px between two schedule labels before the later one is dropped. A
   */5 cron used to paint an unreadable smear of overlapping chips."
  44)

(defn run-on-screen?
  "Whether the run that fired at `timestamp` has any bar in the window."
  [timestamp data timeline now]
  (boolean
   (some (fn [{:keys [executions]}]
           (some #(and (isEqual (:scheduled-at %) timestamp)
                       (ran-in-window? % timeline now))
                 executions))
         data)))

(defn schedule-marks
  "Fire-times to draw, as `[{:timestamp :pos :label? :edge}]`. One inside the
   window sits at its own position; one before it is pinned to the leading border
   if the run it fired still has a bar on screen, so that run stays selectable.

   Only the nearest such run is marked — two markers on one pixel cannot both be
   clicked. There is no trailing case: a run cannot start before its own
   fire-time, so a later fire-time never has a bar inside the window."
  [schedule-timestamps {:keys [started-at ended-at] :as timeline} cell-width data now]
  (let [pos-of   (fn [t] (* (differenceInSeconds t started-at) (/ cell-width 3600)))
        in?      #(isWithinInterval % #js {:start started-at :end ended-at})
        ;; Keyed on the epoch: `distinct` will not dedupe Dates, since CLJS gives them
        ;; value equality but no matching hash.
        all      (->> schedule-timestamps
                      (map (juxt #(.getTime %) identity))
                      (into (sorted-map))
                      vals)
        ;; Nearest first, and short-circuiting: `run-on-screen?` is O(rows x
        ;; executions), and a */5 cron over a week would otherwise run it ~2,000
        ;; times per render to find one marker.
        spilling (->> all
                      (take-while #(isBefore % started-at))
                      reverse
                      (some #(when (run-on-screen? % data timeline now) %)))
        ticks    (first
                  (reduce (fn [[acc last-labelled] timestamp]
                            (let [pos    (pos-of timestamp)
                                  label? (or (nil? last-labelled)
                                             (>= (- pos last-labelled) schedule-label-gap))]
                              [(conj acc {:timestamp timestamp :pos pos :label? label?})
                               (if label? pos last-labelled)]))
                          [[] nil]
                          (filter in? all)))]
    (cond-> ticks
      ;; Inset 2px so the tick and its hit area are not half-clipped.
      spilling (conj {:timestamp spilling :pos 2 :label? true :edge :start}))))


(defn run-extent
  "`{:started-at :ended-at}` spanned by the run that fired at `timestamp` across all
   rows — its first start to its last finish (`now` while one is going) — or nil."
  [timestamp data now]
  (let [runs (for [{:keys [executions]} data
                   e executions
                   :when (and (:started-at e) (isEqual (:scheduled-at e) timestamp))]
               e)]
    (when (seq runs)
      {:started-at (js/Date. (apply min (map #(.getTime (:started-at %)) runs)))
       :ended-at   (js/Date. (apply max (map #(.getTime (or (:finished-at %) now)) runs)))})))
